/**
 * Module Refresh Context
 * Provides a way for full-page modules to register their refresh functions
 * for real-time WebSocket updates
 */

import React, { createContext, useContext, useRef, ReactNode } from 'react';

interface ModuleRefreshContextType {
  registerCalendarRefresh: (refreshFn: () => void) => void;
  registerEmailRefresh: (refreshFn: () => void) => void;
  registerTasksRefresh: (refreshFn: () => void) => void;
  registerKnowledgeRefresh: (refreshFn: () => void) => void;
  registerCurriculumRefresh: (refreshFn: () => void) => void;
  calendarRefreshRef: React.MutableRefObject<(() => void) | null>;
  emailRefreshRef: React.MutableRefObject<(() => void) | null>;
  tasksRefreshRef: React.MutableRefObject<(() => void) | null>;
  knowledgeRefreshRef: React.MutableRefObject<(() => void) | null>;
  curriculumRefreshRef: React.MutableRefObject<(() => void) | null>;
}

const ModuleRefreshContext = createContext<ModuleRefreshContextType | undefined>(undefined);

export const ModuleRefreshProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const calendarRefreshRef = useRef<(() => void) | null>(null);
  const emailRefreshRef = useRef<(() => void) | null>(null);
  const tasksRefreshRef = useRef<(() => void) | null>(null);
  const knowledgeRefreshRef = useRef<(() => void) | null>(null);
  const curriculumRefreshRef = useRef<(() => void) | null>(null);

  const registerCalendarRefresh = (refreshFn: () => void) => {
    calendarRefreshRef.current = refreshFn;
    console.log('📅 Calendar module refresh registered');
  };

  const registerEmailRefresh = (refreshFn: () => void) => {
    emailRefreshRef.current = refreshFn;
    console.log('📧 Email module refresh registered');
  };

  const registerTasksRefresh = (refreshFn: () => void) => {
    tasksRefreshRef.current = refreshFn;
    console.log('✅ Tasks module refresh registered');
  };

  const registerKnowledgeRefresh = (refreshFn: () => void) => {
    knowledgeRefreshRef.current = refreshFn;
    console.log('📚 Knowledge module refresh registered');
  };

  const registerCurriculumRefresh = (refreshFn: () => void) => {
    curriculumRefreshRef.current = refreshFn;
    console.log('📖 Curriculum module refresh registered');
  };

  return (
    <ModuleRefreshContext.Provider
      value={{
        registerCalendarRefresh,
        registerEmailRefresh,
        registerTasksRefresh,
        registerKnowledgeRefresh,
        registerCurriculumRefresh,
        calendarRefreshRef,
        emailRefreshRef,
        tasksRefreshRef,
        knowledgeRefreshRef,
        curriculumRefreshRef,
      }}
    >
      {children}
    </ModuleRefreshContext.Provider>
  );
};

export const useModuleRefresh = (): ModuleRefreshContextType => {
  const context = useContext(ModuleRefreshContext);
  if (!context) {
    throw new Error('useModuleRefresh must be used within ModuleRefreshProvider');
  }
  return context;
};
